// Decodes Blender-authored prefabs (see tools/glb2js.py) into engine meshes.
import { Mesh } from './renderer.js';
import { MODEL_DATA } from '../assets/models.gen.js';
import { col } from './math.js';

let GL = null;
const cache = new Map();
const lut = new Float32Array(256);
for (let i = 0; i < 256; i++) {
  const c = i / 255;
  lut[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
const disabled = typeof location !== 'undefined' && new URLSearchParams(location.search).has('noassets');

export function initAssets(gl) {
  GL = gl;
}

function b64(s) {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Build a GeoBuilder-compatible {v, i} from a quantised part, optionally tinting flagged vertices. */
function decodePart(p, tint) {
  const P = new Uint16Array(b64(p.p).buffer);
  const N = new Int8Array(b64(p.no).buffer);
  const C = b64(p.c);
  const Mt = b64(p.m);
  const T = p.t ? b64(p.t) : null;
  const ib = b64(p.i);
  const I = p.i32 ? new Uint32Array(ib.buffer) : new Uint16Array(ib.buffer);
  const n = p.n;
  const v = new Float32Array(n * 13);
  for (let k = 0; k < n; k++) {
    const o = k * 13;
    v[o] = p.min[0] + (P[k * 3] / 65535) * p.rng[0];
    v[o + 1] = p.min[1] + (P[k * 3 + 1] / 65535) * p.rng[1];
    v[o + 2] = p.min[2] + (P[k * 3 + 2] / 65535) * p.rng[2];
    v[o + 3] = N[k * 3] / 127;
    v[o + 4] = N[k * 3 + 1] / 127;
    v[o + 5] = N[k * 3 + 2] / 127;
    let r = lut[C[k * 3]], g = lut[C[k * 3 + 1]], bl = lut[C[k * 3 + 2]];
    if (tint && T && T[k]) {
      // flagged vertices keep their baked shading (grey value) but take the tint colour
      const lum = Math.max(r, g, bl);
      r = tint[0] * lum; g = tint[1] * lum; bl = tint[2] * lum;
    }
    v[o + 6] = r; v[o + 7] = g; v[o + 8] = bl;
    v[o + 9] = Mt[k * 4] / 255;
    v[o + 10] = Mt[k * 4 + 1] / 255;
    v[o + 11] = (Mt[k * 4 + 2] / 255) * 16;
    v[o + 12] = Mt[k * 4 + 3] / 255;
  }
  return { v, i: I };
}

export function hasModel(name) {
  return !disabled && !!MODEL_DATA[name];
}

/** Returns [{name, pos, fx, mesh}] for a prefab, or null if the asset doesn't exist. */
export function modelParts(name, tintHex = null) {
  if (!hasModel(name)) return null;
  const key = name + '|' + (tintHex || '');
  let parts = cache.get(key);
  if (parts) return parts;
  const tint = tintHex ? col(tintHex) : null;
  parts = MODEL_DATA[name].map((p) => ({ name: p.name, pos: p.pos, fx: p.fx, mesh: new Mesh(GL, decodePart(p, tint)) }));
  cache.set(key, parts);
  return parts;
}

/** Single mesh lookup: first part whose name starts with `partPrefix` (or the first part). */
export function modelMesh(name, partPrefix = null, tintHex = null) {
  const parts = modelParts(name, tintHex);
  if (!parts) return null;
  const p = partPrefix ? parts.find((x) => x.name.startsWith(partPrefix)) : parts[0];
  return p ? p.mesh : null;
}
export function modelPart(name, partPrefix, tintHex = null) {
  const parts = modelParts(name, tintHex);
  return parts ? parts.find((x) => x.name.startsWith(partPrefix)) || null : null;
}
